import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { REC_TYPES, REC_CATEGORIES, AVAILABILITY, LOCATION_SPECIFIC, HOW_FOUND } from "@/lib/recommend";
import { ArrowRight, ArrowLeft, Check } from "lucide-react";

const TOTAL = 8;

export default function Recommend() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [step, setStep] = useState(1);
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);
  const [dup, setDup] = useState(null);
  const [f, setF] = useState({
    rec_type: "", name: "", url: "", social_url: "", category: params.get("category") || "",
    subcategory: "", why: "", availability: "", location: { city: "", state: "", zip: "" },
    how_found: "", submitter_email: "", notify: false,
  });

  useEffect(() => { window.scrollTo({ top: 0 }); }, [step]);

  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const setLoc = (k, v) => setF((p) => ({ ...p, location: { ...p.location, [k]: v } }));

  const canAdvance = () => {
    if (step === 1) return !!f.rec_type;
    if (step === 2) return f.name.trim().length > 0;
    if (step === 4) return !!f.category;
    if (step === 6) return !!f.availability;
    return true; // 3,5,7,8 optional
  };

  const next = () => { if (canAdvance() && step < TOTAL) setStep(step + 1); };
  const back = () => step > 1 ? setStep(step - 1) : nav(-1);
  const pick = (k, v) => { set(k, v); setTimeout(() => setStep((s) => Math.min(s + 1, TOTAL)), 180); };

  const submit = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/recommendations", f);
      setDup(data.possible_duplicate);
      setDone(true);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const onKey = (e) => { if (e.key === "Enter" && step !== 5) { e.preventDefault(); next(); } };

  if (done) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-32 min-h-[70vh] flex flex-col justify-center">
        <div className="eyebrow mb-4 text-[#5FB878]">SUBMITTED</div>
        <h1 data-testid="recommend-thankyou" className="font-display text-5xl md:text-7xl leading-[0.9] mb-6">THANK YOU.</h1>
        <p className="font-serif text-xl text-stone-300 max-w-lg mb-4">You just helped someone find a cheaper way.</p>
        {dup && (
          <p className="font-label text-xs tracking-widest text-[#D4AF37] border border-[#D4AF37]/40 px-4 py-3 max-w-lg mb-8">
            HEADS UP · This may already be in THE CHEAPER WAY as “{dup}”. We'll review either way.
          </p>
        )}
        <Link to="/" data-testid="recommend-back-home"
          className="inline-flex items-center gap-2 font-label text-xs tracking-widest bg-[#EF3340] text-white px-6 py-4 self-start hover:bg-[#cc2a35] transition-colors">
          BACK TO THE CHEAPER WAY <ArrowRight className="w-3 h-3" />
        </Link>
      </div>
    );
  }

  const Choices = ({ field, options, testid }) => (
    <div data-testid={testid} className="grid sm:grid-cols-2 gap-2 max-w-2xl">
      {options.map((o) => (
        <button key={o} onClick={() => pick(field, o)}
          data-testid={`choice-${o.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
          className={`text-left font-label text-xs tracking-widest border px-5 py-4 transition-colors ${f[field] === o ? "bg-[#EF3340] text-white border-[#EF3340]" : "hairline text-stone-300 hover:border-[#EF3340] hover:text-[#EF3340]"}`}>
          {o}
        </button>
      ))}
    </div>
  );

  const inputCls = "w-full bg-transparent border hairline px-5 py-5 text-xl md:text-2xl font-display text-stone-100 placeholder:text-stone-600 outline-none focus:border-[#EF3340] transition-colors";

  return (
    <div className="max-w-3xl mx-auto px-6 py-12 md:py-16 min-h-[70vh]" onKeyDown={onKey}>
      {/* progress */}
      <div className="flex items-center justify-between mb-10">
        <Link to="/" className="font-label text-[10px] tracking-widest text-stone-500">THE CHEAPER WAY / RECOMMEND</Link>
        <span className="font-label text-[10px] tracking-widest text-stone-500">{step} / {TOTAL}</span>
      </div>
      <div className="h-[2px] bg-white/10 mb-12">
        <div className="h-full bg-[#EF3340] transition-all duration-300" style={{ width: `${(step / TOTAL) * 100}%` }} />
      </div>

      <div className="fade-up" key={step}>
        {step === 1 && (
          <>
            <h2 className="font-display text-3xl md:text-5xl leading-[0.95] mb-8">WHAT ARE YOU<br />RECOMMENDING?</h2>
            <Choices field="rec_type" options={REC_TYPES} testid="step-rec-type" />
          </>
        )}
        {step === 2 && (
          <>
            <h2 className="font-display text-3xl md:text-5xl leading-[0.95] mb-8">WHAT'S THE NAME?</h2>
            <input autoFocus value={f.name} onChange={(e) => set("name", e.target.value)} data-testid="rec-name-input"
              placeholder="Name of the source, service, deal, or resource" className={inputCls} />
          </>
        )}
        {step === 3 && (
          <>
            <h2 className="font-display text-3xl md:text-5xl leading-[0.95] mb-8">WHERE CAN WE<br />FIND IT?</h2>
            <input autoFocus value={f.url} onChange={(e) => set("url", e.target.value)} data-testid="rec-url-input"
              placeholder="Website URL" className={inputCls + " mb-4"} />
            <input value={f.social_url} onChange={(e) => set("social_url", e.target.value)} data-testid="rec-social-input"
              placeholder="Social media / other link (optional)" className={inputCls} />
          </>
        )}
        {step === 4 && (
          <>
            <h2 className="font-display text-3xl md:text-5xl leading-[0.95] mb-8">WHAT CATEGORY DOES<br />IT BELONG IN?</h2>
            <Choices field="category" options={REC_CATEGORIES} testid="step-category" />
          </>
        )}
        {step === 5 && (
          <>
            <h2 className="font-display text-3xl md:text-5xl leading-[0.95] mb-8">WHAT MAKES IT<br />WORTH KNOWING?</h2>
            <textarea autoFocus value={f.why} onChange={(e) => set("why", e.target.value)} data-testid="rec-why-input" rows={5}
              placeholder="Tell us why someone should know about this…"
              className="w-full bg-transparent border hairline px-5 py-5 text-lg font-serif text-stone-100 placeholder:text-stone-600 outline-none focus:border-[#EF3340] transition-colors resize-none" />
          </>
        )}
        {step === 6 && (
          <>
            <h2 className="font-display text-3xl md:text-5xl leading-[0.95] mb-8">WHERE IS IT<br />AVAILABLE?</h2>
            <Choices field="availability" options={AVAILABILITY} testid="step-availability" />
            {LOCATION_SPECIFIC.includes(f.availability) && (
              <div className="grid sm:grid-cols-3 gap-3 mt-6 max-w-2xl">
                <input value={f.location.city} onChange={(e) => setLoc("city", e.target.value)} placeholder="City" data-testid="rec-city-input"
                  className="bg-transparent border hairline px-4 py-3 font-label text-sm outline-none focus:border-[#EF3340]" />
                <input value={f.location.state} onChange={(e) => setLoc("state", e.target.value)} placeholder="State" data-testid="rec-state-input"
                  className="bg-transparent border hairline px-4 py-3 font-label text-sm outline-none focus:border-[#EF3340]" />
                <input value={f.location.zip} onChange={(e) => setLoc("zip", e.target.value)} placeholder="ZIP (optional)" data-testid="rec-zip-input"
                  className="bg-transparent border hairline px-4 py-3 font-label text-sm outline-none focus:border-[#EF3340]" />
              </div>
            )}
          </>
        )}
        {step === 7 && (
          <>
            <div className="eyebrow mb-3 text-stone-500">OPTIONAL</div>
            <h2 className="font-display text-3xl md:text-5xl leading-[0.95] mb-8">HOW DID YOU<br />FIND IT?</h2>
            <Choices field="how_found" options={HOW_FOUND} testid="step-how-found" />
          </>
        )}
        {step === 8 && (
          <>
            <div className="eyebrow mb-3 text-stone-500">OPTIONAL</div>
            <h2 className="font-display text-3xl md:text-5xl leading-[0.95] mb-6">WANT US TO LET YOU<br />KNOW IF WE ADD IT?</h2>
            <input autoFocus type="email" value={f.submitter_email} onChange={(e) => set("submitter_email", e.target.value)} data-testid="rec-email-input"
              placeholder="Email address (optional)" className={inputCls + " mb-5"} />
            <label className="flex items-center gap-3 cursor-pointer">
              <button type="button" onClick={() => set("notify", !f.notify)} data-testid="rec-notify-toggle"
                className={`w-6 h-6 border flex items-center justify-center transition-colors ${f.notify ? "bg-[#EF3340] border-[#EF3340]" : "hairline"}`}>
                {f.notify && <Check className="w-4 h-4 text-white" />}
              </button>
              <span className="font-serif text-base text-stone-300">Yes, email me when this recommendation is reviewed.</span>
            </label>
          </>
        )}
      </div>

      {/* nav */}
      <div className="flex items-center gap-3 mt-14">
        <button onClick={back} data-testid="rec-back-btn"
          className="inline-flex items-center gap-2 font-label text-xs tracking-widest text-stone-400 hover:text-stone-200 transition-colors">
          <ArrowLeft className="w-3 h-3" /> BACK
        </button>
        <div className="flex-1" />
        {step < TOTAL ? (
          <button onClick={next} disabled={!canAdvance()} data-testid="rec-next-btn"
            className="inline-flex items-center gap-2 font-label text-xs tracking-widest bg-[#EF3340] text-white px-6 py-4 hover:bg-[#cc2a35] transition-colors disabled:opacity-40">
            {[3, 7, 8].includes(step) ? "CONTINUE" : "NEXT"} <ArrowRight className="w-3 h-3" />
          </button>
        ) : (
          <button onClick={submit} disabled={loading} data-testid="rec-submit-btn"
            className="inline-flex items-center gap-2 font-label text-xs tracking-widest bg-[#EF3340] text-white px-6 py-4 hover:bg-[#cc2a35] transition-colors disabled:opacity-40">
            {loading ? "SENDING…" : "SUBMIT RECOMMENDATION"} <ArrowRight className="w-3 h-3" />
          </button>
        )}
      </div>
    </div>
  );
}
